import { createContext, useContext, ReactNode } from "react";
import { getVariantForProductKey } from "./products";
import type { Subscription } from "./types";

interface SubscriptionContextValue {
  subscriptions: Subscription[];
  isReorderMode: boolean;
  /**
   * Get subscription for a product by matching product_key to productId.
   * Both use underscore format: "hsc_bio_slv"
   */
  getSubscriptionForProduct: (productId: string) => Subscription | null;
  /**
   * Get the variant ID from a subscription's product_key.
   * e.g., "hsc_std2_slv" -> "silver", "hsc_std2_brz" -> "bronze"
   */
  getVariantFromSubscription: (subscription: Subscription) => string | undefined;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

/**
 * Convert product_key to productId format.
 * Now a no-op since both use underscore format (e.g., "hsc_bio_slv").
 */
function productKeyToProductId(productKey: string): string {
  return productKey;
}

interface SubscriptionProviderProps {
  children: ReactNode;
  subscriptions: Subscription[];
}

export function SubscriptionProvider({ children, subscriptions }: SubscriptionProviderProps) {
  const getSubscriptionForProduct = (productId: string): Subscription | null => {
    // Try exact match first
    const exactMatch = subscriptions.find(
      (sub) => productKeyToProductId(sub.product_key) === productId
    );
    if (exactMatch) return exactMatch;

    // For multi-variant products, match base product (without variant)
    // e.g., productId "hsc_std2" should match subscription "hsc_std2_slv"
    const baseMatch = subscriptions.find((sub) => {
      const subProductId = productKeyToProductId(sub.product_key);
      // Check if subscription's productId starts with the requested productId
      // This handles cases like "hsc_std2" matching "hsc_std2_slv" subscription
      // But we need to be careful: "hsc_bio_slv" (productId) should match "hsc_bio_slv"
      // The productId for multi-variant is just "hsc_std2", for single-variant it's "hsc_bio_slv"
      return subProductId === productId || subProductId.startsWith(productId + "_");
    });

    return baseMatch || null;
  };

  const getVariantFromSubscription = (subscription: Subscription): string | undefined => {
    return getVariantForProductKey(subscription.product_key);
  };

  const value: SubscriptionContextValue = {
    subscriptions,
    isReorderMode: subscriptions.length > 0,
    getSubscriptionForProduct,
    getVariantFromSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context.
 * Returns null values if not in reorder mode (no provider).
 */
export function useSubscriptions(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);

  // If no provider, return default "not in reorder mode" values
  if (!ctx) {
    return {
      subscriptions: [],
      isReorderMode: false,
      getSubscriptionForProduct: () => null,
      getVariantFromSubscription: () => undefined,
    };
  }

  return ctx;
}
